/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The one telephony engine.
 *
 * Holds the Twilio Device, the current call, presence and the wrap-up draft.
 * The dock and (later) the Call Console are two skins over this single state,
 * which is what stops them becoming two competing call UIs.
 *
 * Mounted above <Routes> so a call survives navigation, and always rendered --
 * never conditionally, because a provider that disappears when unentitled
 * changes hook order. It is inert instead: no token is fetched and no Device
 * is constructed unless telephony is both entitled and configured.
 *
 * Tab leadership matters more than it looks. Agents keep several CRM tabs
 * open, Twilio rings every registered instance of an identity, and the result
 * is several tabs ringing at once with only one able to answer. A
 * BroadcastChannel election means exactly one tab registers a Device.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "auth/Auth";
import { useWebSocket } from "context/WebSocketContext";
import useTwilioDevice from "./useTwilioDevice";
import * as ringer from "./ringer";
import * as notifier from "./notifier";
import { CALL_STATE, LIVE_STATES } from "./callStates";
import { createTabLeader } from "./tabLeader";

const TelephonyContext = createContext(null);

const CHANNEL = "pearl-telephony";
const MIC_PROMPTED_KEY = "pearl.telephony.micPrompted";
// Agents mid-form find an unrequested navigation hostile; the toggle is theirs.
const AUTO_POP_KEY = "pearl.telephony.autoPop";
// The agent's chosen outbound line, when the tenant has more than one.
const FROM_NUMBER_KEY = "pearl.telephony.fromNumberId";

export function useTelephony() {
  const ctx = useContext(TelephonyContext);
  if (!ctx) {
    throw new Error("useTelephony must be used inside <TelephonyProvider>");
  }
  return ctx;
}

export function TelephonyProvider({ children }) {
  const { axiosInstance, isAuthenticated, isAuthReady } = useAuth();
  const { telephonyEvent } = useWebSocket();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [settingsChecked, setSettingsChecked] = useState(false);
  const [presence, setPresence] = useState("offline");
  const [callState, setCallState] = useState(CALL_STATE.IDLE);
  const [activeCall, setActiveCall] = useState(null);      // our conversation row
  const [durationSec, setDurationSec] = useState(0);
  const [muted, setMuted] = useState(false);
  const [micError, setMicError] = useState(null);
  const [isLeader, setIsLeader] = useState(false);
  // The page (path) of the tab holding the softphone, when it is not this one.
  const [leaderElsewhere, setLeaderElsewhere] = useState(null);
  const [inlineHostActive, setInlineHostActive] = useState(false);
  const [notesDraft, setNotesDraft] = useState({});
  const [callerIds, setCallerIds] = useState([]);
  const [fromNumberId, setFromNumberIdState] = useState(() => {
    try {
      const raw = window.localStorage.getItem(FROM_NUMBER_KEY);
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  });

  const twilioCallRef = useRef(null);
  // Which conversation this tab has already bound, so the websocket event, the
  // Device's customParameters and the ?call= deep link can all race harmlessly.
  const adoptedIdRef = useRef(null);
  const durationTimerRef = useRef(null);
  const channelRef = useRef(null);
  const leaderRef = useRef(null);
  const tabIdRef = useRef(
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const enabled = Boolean(
    isAuthReady && isAuthenticated && settings?.enabled && settings?.is_configured
  );

  // ------------------------------------------------------------ settings

  useEffect(() => {
    let cancelled = false;
    if (!isAuthReady || !isAuthenticated) return undefined;

    (async () => {
      try {
        // /telephony/status, not /telephony/settings. The settings payload is
        // gated on MANAGE_TELEPHONY because it carries credentials and the
        // webhook token -- correctly so -- but asking it "is there a phone
        // system here" made every ordinary agent's probe 403, and the console
        // then told them their organisation had no telephony at all. Status is
        // two booleans and needs only a signed-in user.
        const res = await axiosInstance.get("/telephony/status");
        if (!cancelled) setSettings(res.data);
      } catch (err) {
        // Genuinely inert: the tenant has no telephony, or has not finished
        // setting it up. Rendering an idle console is the right answer.
        if (!cancelled) setSettings(null);

        // Anything other than a 404 means the probe itself failed rather than
        // answering "no". Worth a line in the console, because the symptom --
        // an agent staring at "telephony is not set up" on a tenant where it
        // plainly is -- gives no hint that the request was refused.
        const status = err?.response?.status;
        if (status && status !== 404) {
          // eslint-disable-next-line no-console
          console.warn(
            `[telephony] status probe failed with ${status}; treating telephony as unavailable.`,
            err?.response?.data
          );
        }
      } finally {
        if (!cancelled) setSettingsChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, isAuthReady, isAuthenticated]);

  // ------------------------------------------------------ leader election

  useEffect(() => {
    if (!enabled || typeof BroadcastChannel === "undefined") {
      // Without BroadcastChannel (or when inert) act as sole tab.
      setIsLeader(Boolean(enabled));
      return undefined;
    }

    const channel = new BroadcastChannel(CHANNEL);
    channelRef.current = channel;
    const leader = createTabLeader({
      channel,
      myId: tabIdRef.current,
      where: () => window.location.pathname,
      onChange: ({ isLeader: lead, leaderWhere }) => {
        setIsLeader(lead);
        setLeaderElsewhere(leaderWhere);
      },
    });
    leaderRef.current = leader;

    const onUnload = () => leader.release();
    window.addEventListener("pagehide", onUnload);

    return () => {
      window.removeEventListener("pagehide", onUnload);
      leader.stop();
      leaderRef.current = null;
      channel.close();
      channelRef.current = null;
    };
  }, [enabled]);

  // --------------------------------------------------------------- device

  const fetchToken = useCallback(async () => {
    const res = await axiosInstance.get("/telephony/voice-token");
    return res.data;
  }, [axiosInstance]);

  const reportDeviceStatus = useCallback(
    async (status) => {
      try {
        await axiosInstance.put("/telephony/agent/device", { device_status: status });
      } catch {
        /* presence is reconciled by the next poll */
      }
    },
    [axiosInstance]
  );

  /**
   * Bind a conversation to this tab, fetching the full row.
   *
   * The single writer for `activeCall` outside placeCall. Three callers reach
   * it -- the `call_ringing` socket event, the Twilio Device's customParameters
   * and the console's ?call= deep link -- and every one of them used to be
   * optional, which is how an agent ended up on a live call with a dead
   * "who was this about?" panel. Idempotent: adopting the call already held is
   * a no-op, so the three racing is fine.
   */
  const adoptConversation = useCallback(
    async (conversationId, optimistic = null) => {
      const id = Number(conversationId);
      if (!id) return null;

      // A ref, not the state, is what makes this idempotent. Two of the three
      // callers can fire in the same tick, and a setState updater does not run
      // synchronously -- reading the result back here would still say "not
      // held" and issue a second GET for the same call.
      if (adoptedIdRef.current === id) return null;
      adoptedIdRef.current = id;

      if (optimistic) {
        setActiveCall((current) =>
          current?.id === id ? current : { id, ...optimistic }
        );
      }

      try {
        const res = await axiosInstance.get(`/telephony/calls/${id}`);
        setActiveCall(res.data);
        // A call the server still holds in wrap-up comes back in wrap-up. A
        // reload, or the classic dock opening the console, used to drop the
        // in-memory state to idle, which hid the wrap-up form entirely.
        if (res.data?.status === CALL_STATE.WRAP_UP) {
          setCallState((current) => (current === CALL_STATE.IDLE ? CALL_STATE.WRAP_UP : current));
        }
        return res.data;
      } catch {
        // The optimistic row above still carries the number and the name, so
        // the console is usable even when this fetch loses a race with the
        // conversation being committed. Release the claim so a later caller
        // can retry rather than being locked out by the failed attempt.
        if (adoptedIdRef.current === id && !optimistic) adoptedIdRef.current = null;
        return null;
      }
    },
    [axiosInstance]
  );

  const handleIncoming = useCallback((call) => {
    twilioCallRef.current = call;
    setCallState(CALL_STATE.RINGING);
    ringer.start();

    // Twilio hands query params from the `client:<id>?conv=N` URI through as
    // customParameters (a Map). Reading it here means the ring itself binds the
    // call, independently of whether the websocket event arrives.
    try {
      const params = call.customParameters;
      const conv = params?.get ? params.get("conv") : null;
      if (conv) adoptConversation(conv, { direction: "inbound" });
    } catch {
      /* no custom parameters on this call; the socket event is the fallback */
    }

    call.on("cancel", () => {
      ringer.stop();
      notifier.clearAll();
      twilioCallRef.current = null;
      setCallState(CALL_STATE.IDLE);
    });
    call.on("disconnect", () => {
      ringer.stop();
      notifier.clearAll();
      twilioCallRef.current = null;
      setCallState(CALL_STATE.WRAP_UP);
    });
  }, [adoptConversation]);

  const {
    registered,
    deviceError,
    connect,
    disconnect,
    callOut,
    clearError,
  } = useTwilioDevice({
    enabled,
    fetchToken,
    onIncoming: handleIncoming,
    onRegistrationChange: reportDeviceStatus,
  });

  // Only the leader tab registers, so only one tab can ring.
  useEffect(() => {
    if (!enabled || !isLeader) return undefined;
    let cancelled = false;
    connect().catch(() => {
      if (!cancelled) {
        /* surfaced through deviceError */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, isLeader, connect]);

  useEffect(() => {
    if (!enabled && registered) disconnect();
  }, [enabled, registered, disconnect]);

  // ------------------------------------------------------------- duration

  useEffect(() => {
    const running = callState === CALL_STATE.CONNECTED || callState === CALL_STATE.ON_HOLD;
    if (!running) {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
      if (callState === CALL_STATE.IDLE) setDurationSec(0);
      return undefined;
    }
    // One interval for the whole app; components read the number.
    durationTimerRef.current = setInterval(() => setDurationSec((s) => s + 1), 1000);
    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
    };
  }, [callState]);

  // ------------------------------------------------------------- presence

  const refreshPresence = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/telephony/agent/state");
      setPresence(res.data.state);
    } catch {
      /* leave as-is */
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (enabled) refreshPresence();
  }, [enabled, refreshPresence]);

  const ensureMicrophone = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError({ code: "unsupported", message: "This browser cannot access a microphone." });
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release immediately: the Device opens its own stream per call, and
      // holding this one keeps the browser's recording indicator lit all day.
      stream.getTracks().forEach((t) => t.stop());
      window.localStorage.setItem(MIC_PROMPTED_KEY, "1");
      setMicError(null);
      return true;
    } catch (err) {
      setMicError({
        code: err?.name === "NotFoundError" ? "no_device" : "denied",
        message:
          err?.name === "NotFoundError"
            ? "No microphone was found."
            : "Microphone access is blocked, so calls cannot be answered.",
      });
      return false;
    }
  }, []);

  const changePresence = useCallback(
    async (next) => {
      // Going available is a user gesture, which is the only moment the
      // browser will let us unlock audio for a later incoming ring.
      if (next === "available") {
        ringer.prime();
        // Same gesture, same reasoning: this is the one moment the browser
        // will honour both the audio unlock and a notification prompt, and an
        // agent declaring themselves ready for calls is who the prompt is for.
        notifier.requestPermission();
        const ok = await ensureMicrophone();
        if (!ok) return false;
      }
      const previous = presence;
      setPresence(next);
      try {
        const res = await axiosInstance.put("/telephony/agent/state", { state: next });
        setPresence(res.data.state);
        return true;
      } catch {
        setPresence(previous);
        return false;
      }
    },
    [axiosInstance, ensureMicrophone, presence]
  );

  // ---------------------------------------------------------- caller ids

  useEffect(() => {
    let cancelled = false;
    if (!enabled) return undefined;
    (async () => {
      try {
        const res = await axiosInstance.get("/telephony/caller-ids");
        if (!cancelled) setCallerIds(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Only the label depends on this; the server still picks a number.
        if (!cancelled) setCallerIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, axiosInstance]);

  const setFromNumberId = useCallback((id) => {
    setFromNumberIdState(id);
    try {
      if (id) window.localStorage.setItem(FROM_NUMBER_KEY, String(id));
      else window.localStorage.removeItem(FROM_NUMBER_KEY);
    } catch {
      /* private mode */
    }
  }, []);

  // A stored choice for a number that has since been removed falls back to
  // the default rather than sending an id the server will refuse.
  const fromNumber =
    callerIds.find((n) => n.id === fromNumberId) ||
    callerIds.find((n) => n.is_default) ||
    callerIds[0] ||
    null;

  // ----------------------------------------------------------- call verbs

  const placeCall = useCallback(
    async ({ to, context = {} }) => {
      if (!enabled) throw new Error("Telephony is not set up.");
      if (!isLeader) throw new Error("The softphone is active in another tab.");

      const ok = await ensureMicrophone();
      if (!ok) throw new Error("Microphone access is required to place a call.");

      setCallState(CALL_STATE.CONNECTING);
      try {
        // The record is created server-side first, so a call can never exist
        // at Twilio without a corresponding record in Pearl.
        const res = await axiosInstance.post("/telephony/calls", {
          to,
          ...(fromNumber ? { from_number_id: fromNumber.id } : {}),
          ...context,
        });
        const conversation = res.data.conversation;
        setActiveCall(conversation);
        // Claim it here too, so a call_ringing event for our own outbound call
        // does not re-fetch what we already hold.
        adoptedIdRef.current = conversation.id;

        const call = await callOut({ conv: String(conversation.id), To: to });
        twilioCallRef.current = call;

        call.on("accept", () => setCallState(CALL_STATE.CONNECTED));
        call.on("disconnect", () => {
          twilioCallRef.current = null;
          setCallState(CALL_STATE.WRAP_UP);
        });
        call.on("cancel", () => {
          twilioCallRef.current = null;
          setCallState(CALL_STATE.IDLE);
        });
        call.on("error", () => {
          twilioCallRef.current = null;
          setCallState(CALL_STATE.IDLE);
        });

        return conversation;
      } catch (err) {
        setCallState(CALL_STATE.IDLE);
        setActiveCall(null);
        adoptedIdRef.current = null;
        throw err;
      }
    },
    [axiosInstance, callOut, enabled, ensureMicrophone, isLeader, fromNumber]
  );

  const acceptCall = useCallback(async () => {
    const call = twilioCallRef.current;
    if (!call) return;
    ringer.stop();
    notifier.clearAll();
    const ok = await ensureMicrophone();
    if (!ok) return;
    setCallState(CALL_STATE.CONNECTING);
    call.accept();
    call.on("accept", () => setCallState(CALL_STATE.CONNECTED));
  }, [ensureMicrophone]);

  const rejectCall = useCallback(() => {
    const call = twilioCallRef.current;
    ringer.stop();
    notifier.clearAll();
    if (call) call.reject();
    twilioCallRef.current = null;
    setCallState(CALL_STATE.IDLE);
  }, []);

  const hangUp = useCallback(() => {
    const call = twilioCallRef.current;
    const conversationId = activeCall?.id;
    ringer.stop();
    notifier.clearAll();
    // Local first, so the agent sees the call end the instant they click.
    if (call) call.disconnect();
    twilioCallRef.current = null;
    setCallState((s) => (s === CALL_STATE.IDLE ? s : CALL_STATE.WRAP_UP));

    // Then tell the server to end it for the customer too. Dropping our own
    // leg leaves them in the conference on hold music -- the agent leg is
    // deliberately endConferenceOnExit=false so that transfer and barge work.
    if (conversationId) {
      axiosInstance
        .post(`/telephony/calls/${conversationId}/hangup`)
        .catch(() => {
          /* The conference-end webhook still reconciles this; the agent is
             already off the call either way. */
        });
    }
  }, [activeCall, axiosInstance]);

  const toggleMute = useCallback(() => {
    const call = twilioCallRef.current;
    if (!call) return;
    const next = !muted;
    call.mute(next);
    setMuted(next);
  }, [muted]);

  const sendDigits = useCallback((digits) => {
    const call = twilioCallRef.current;
    if (call) call.sendDigits(String(digits));
  }, []);

  const submitDisposition = useCallback(
    async ({ dispositionId, notes }) => {
      if (!activeCall) return null;
      const res = await axiosInstance.post(
        `/telephony/calls/${activeCall.id}/disposition`,
        { disposition_id: dispositionId || null, notes: notes || null }
      );
      setActiveCall(null);
      adoptedIdRef.current = null;
      setCallState(CALL_STATE.IDLE);
      setDurationSec(0);
      setMuted(false);
      setNotesDraft((d) => {
        const next = { ...d };
        delete next[res.data.id];
        return next;
      });
      refreshPresence();
      return res.data;
    },
    [activeCall, axiosInstance, refreshPresence]
  );

  /**
   * Leave wrap-up without dispositioning the call.
   *
   * The server already frees the agent on its own timer (sweep_wrap_up), so
   * this is about not making the agent wait for it. The notes draft is kept
   * deliberately: skipping wrap-up should not throw away what they typed
   * during the call, and the call can still be dispositioned later from its
   * record.
   */
  const skipWrapUp = useCallback(() => {
    setCallState((current) =>
      current === CALL_STATE.WRAP_UP ? CALL_STATE.IDLE : current
    );
    setActiveCall(null);
    adoptedIdRef.current = null;
    setDurationSec(0);
    setMuted(false);
    // Best effort: tell the server we are ready for the next call rather than
    // sitting out the remaining wrap-up seconds.
    changePresence("available").catch(() => {
      /* the sweep will release us anyway */
    });
  }, [changePresence]);

  /** Notes survive a route change or a socket blip mid-call. */
  const saveNotesDraft = useCallback((conversationId, text) => {
    setNotesDraft((d) => ({ ...d, [conversationId]: text }));
  }, []);

  // Server-side call events. The server knows about an inbound call before the
  // Device rings, so the console can pop with the caller's record already
  // loaded rather than a spinner.
  useEffect(() => {
    if (!telephonyEvent || !enabled) return;
    const { type, payload } = telephonyEvent;

    if (type === "call_ringing" && payload?.conversation_id) {
      // The event already carries the caller the server resolved on the
      // inbound webhook, so the dock and the desktop notification can name
      // them on the first frame instead of showing the number and correcting
      // itself once the GET lands.
      adoptConversation(payload.conversation_id, {
        direction: payload.direction || "inbound",
        from_address: payload.from,
        to_address: payload.to,
        caller_name: payload.caller_name || null,
        queue_id: payload.queue_id,
      });

      const number = payload.direction === "outbound" ? payload.to : payload.from;
      notifier.startTitleFlash(
        payload.caller_name ? `Call: ${payload.caller_name}` : "Incoming call"
      );
      notifier.showIncoming({
        name: payload.caller_name,
        number,
        onClick: () => navigate(`/call-console?call=${payload.conversation_id}`),
      });

      const autoPop = window.localStorage.getItem(AUTO_POP_KEY) !== "0";
      if (autoPop && window.location.pathname !== "/call-console") {
        navigate(`/call-console?call=${payload.conversation_id}`);
      }
    }

    if (type === "call_ended" && payload?.conversation_id) {
      // call_ended is published to the whole tenant, not to the agent on the
      // call. Without this check every agent's live call flipped to wrap-up
      // whenever anyone else's call ended -- which read as "a second call
      // cut the first one off".
      const mine =
        adoptedIdRef.current != null &&
        String(payload.conversation_id) === String(adoptedIdRef.current);
      if (mine) {
        notifier.clearAll();
        // The dock and console read callState; the Twilio Call object may have
        // already gone, so reconcile from the server's view.
        setCallState((current) =>
          current === CALL_STATE.IDLE ? current : CALL_STATE.WRAP_UP
        );
      }
    }
  }, [telephonyEvent, enabled, adoptConversation, navigate]);

  // Backstop for the alerts. Every path that ends a ring already clears them,
  // but "every path" is exactly the kind of claim that stops being true after
  // the next edit, and the failure mode is a notification and a blinking title
  // that outlive the call. Tie them to the state instead of trusting the
  // handlers.
  useEffect(() => {
    if (callState !== CALL_STATE.RINGING) notifier.clearAll();
  }, [callState]);

  useEffect(() => () => notifier.clearAll(), []);

  // Warn before closing a tab that is holding a live call.
  useEffect(() => {
    if (!LIVE_STATES.has(callState)) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    const onPageHide = () => {
      // Best-effort: lets the server release the agent rather than waiting for
      // a reservation to time out.
      if (activeCall?.id && navigator.sendBeacon) {
        try {
          navigator.sendBeacon(`/telephony/calls/${activeCall.id}/agent-left`);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [callState, activeCall]);

  const value = useMemo(
    () => ({
      enabled,
      settingsChecked,
      settings,
      isLeader,
      leaderElsewhere,
      // Move the softphone to this tab; the tab holding it gives it up.
      takeOverSoftphone: () => leaderRef.current?.takeOver(),
      registered,
      deviceError,
      clearError,
      micError,
      dismissMicError: () => setMicError(null),
      ensureMicrophone,
      presence,
      changePresence,
      refreshPresence,
      callState,
      activeCall,
      durationSec,
      muted,
      placeCall,
      adoptConversation,
      acceptCall,
      rejectCall,
      hangUp,
      toggleMute,
      sendDigits,
      submitDisposition,
      skipWrapUp,
      notesDraft,
      saveNotesDraft,
      inlineHostActive,
      setInlineHostActive,
      callerIds,
      fromNumber,
      setFromNumberId,
      autoPop: typeof window !== "undefined"
        ? window.localStorage.getItem(AUTO_POP_KEY) !== "0"
        : true,
      setAutoPop: (on) => window.localStorage.setItem(AUTO_POP_KEY, on ? "1" : "0"),
    }),
    [
      enabled, settingsChecked, settings, isLeader, leaderElsewhere, registered, deviceError, clearError,
      micError, ensureMicrophone, presence, changePresence, refreshPresence, callState,
      activeCall, durationSec, muted, placeCall, adoptConversation, acceptCall,
      rejectCall, hangUp,
      toggleMute, sendDigits, submitDisposition, skipWrapUp, notesDraft, saveNotesDraft,
      inlineHostActive, callerIds, fromNumber, setFromNumberId,
    ]
  );

  return <TelephonyContext.Provider value={value}>{children}</TelephonyContext.Provider>;
}

export default TelephonyContext;
